import classNames from 'classnames';
import { type ComponentType, memo, type ReactNode } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { Close, Search as SearchIcon } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';
import { useBreakpointsContext } from '@air/provider-media-query';

import { MobilePageHeader } from '~/components/Layouts/HomeLayout/HomeLayoutHeader/MobilePageHeader';
import { type BasePrivateSearchProps } from '~/components/Search/PrivateSearch';
import { SearchRow } from '~/components/Shared/HeaderBar/SearchRow';
import { useMobileSearchBar } from '~/hooks/search/useMobileSearchBar';

export type PrivateHeaderSearchRowProps = {
  isSearchOpen?: boolean;
  title?: string;
  searchPlaceholder?: string;
  trackSearchBarLocation: AirActionTrackingLocation | string;
  searchRowLeftAdornment?: ReactNode;
  searchRowRightAdornment?: ReactNode;
  searchBoxClassName?: string;
  filtersButton?: ReactNode;
  Search: ComponentType<BasePrivateSearchProps>;
};

export const PrivateHeaderSearchRow = memo(
  ({
    isSearchOpen,
    title,
    searchPlaceholder,
    trackSearchBarLocation,
    searchBoxClassName,
    searchRowLeftAdornment,
    searchRowRightAdornment,
    filtersButton,
    Search,
  }: PrivateHeaderSearchRowProps) => {
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { openMobileSearch, isMobileSearchOpen, closeMobileSearch } = useMobileSearchBar();

    return (
      <SearchRow className="searchRow">
        {!isMobileSearchOpen && !isSearchOpen && !isAboveMediumScreen && (
          <MobilePageHeader title={title} isCentered={false} />
        )}
        <div className={classNames('flex grow items-center justify-end md:justify-start', searchBoxClassName)}>
          {searchRowLeftAdornment}
          {isAboveMediumScreen || isMobileSearchOpen || isSearchOpen ? (
            <div className="flex w-full items-center gap-2">
              <div className="relative w-full">
                <Search
                  defaultOpen={isMobileSearchOpen}
                  placeholder={searchPlaceholder}
                  trackLocation={trackSearchBarLocation}
                />
                {isMobileSearchOpen && (
                  <IconButton
                    appearance="ghost"
                    className="absolute top-2 right-2"
                    color="grey"
                    icon={Close}
                    label="Close search"
                    onClick={closeMobileSearch}
                    size="small"
                  />
                )}
              </div>
            </div>
          ) : (
            <IconButton size="medium" onClick={openMobileSearch} appearance="ghost" color="grey" label="Search">
              <SearchIcon className="size-4" />
            </IconButton>
          )}
          {!!filtersButton && <div className="ml-3">{filtersButton}</div>}
        </div>
        {searchRowRightAdornment}
      </SearchRow>
    );
  },
);

PrivateHeaderSearchRow.displayName = 'PrivateHeaderSearch';
